/**
 * Deterministic SVG text serialization for export.
 *
 * Eliminates html2canvas's text-engine divergence by replacing every
 * text-bearing HTML div in the cloned DOM with an equivalent inline
 * <svg><text>. The SVG renders via the browser's normal font path —
 * the same path the editor uses — so the rasterized result matches
 * the editor pixel-for-pixel within antialiasing tolerance.
 *
 * Critical correctness rule: **never re-derive line breaks.** Browser
 * text wrap depends on font metrics that canvas2d.measureText doesn't
 * always match (font cache state, OS-level subpixel rounding, kerning
 * tables). Predicting wrap is therefore unsafe — predictions diverge
 * from actual layout and cause mid-word truncation / extra wraps.
 *
 * Instead: **read the actual visual lines from the live element via the
 * Range API.** Each Range.getBoundingClientRect() returns where the
 * browser actually placed that text fragment. Group fragments by Y to
 * recover the exact line breakdown the editor displays, then emit one
 * <tspan> per recovered line at the live element's Y positions.
 *
 * The live DOM is never touched; only the cloned subtree handed in by
 * html2canvas's onclone hook is mutated.
 *
 * Identification: deterministic `data-export-type="text"` attribute placed
 * by CanvasEditor on its text branch only. Never serialize UI labels,
 * overlays, tooltips, controls, or any foreign HTML node — only nodes the
 * editor explicitly authored as design text. Gradient text emitted via
 * the editor's existing SVG path retains its nested <svg> and is left
 * alone (gradient defs + transforms already preserved by html2canvas's
 * SVG renderer).
 *
 * Returns: { converted, failures, skippedGradient, skippedNoLive }
 */

import { serializeTextAsForeignObject, whitespaceDiagnostic } from './exportTextForeignObject';
import {
  computeTextSignature,
  getCachedStrategy,
  engineDefaultStrategy,
} from './exportBrowserEngine';

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Per-element strategy picker. Returns one of:
 *   - 'foreignObject' → <svg><foreignObject> wrapping the live HTML
 *                       subtree (PRIMARY — browser HTML engine handles
 *                       all layout/wrap/spacing/kerning)
 *   - 'svg-word'      → per-word browser-positioned <svg><text> (LEGACY
 *                       FALLBACK — only when foreignObject is unavailable
 *                       or explicitly opted out, since SVG text
 *                       reconstruction has been the source of repeated
 *                       layout corruption: word collisions, baseline
 *                       drift, whitespace loss)
 *
 * Precedence:
 *   1. Per-element `data-export-text-strategy` attribute (manual override)
 *   2. `window.__exportTextStrategy[id]` map (debugger override)
 *   3. `window.__exportTextStrategy.default` (force a strategy globally)
 *   4. Strategy cache hit by element signature
 *   5. Default → 'foreignObject' for every text node.
 *
 * Returns { strategy, reason, signature } so callers (diagnostics + cache
 * writer) know which strategy ran and what signature it was keyed under.
 */
function pickTextStrategy(liveEl, cs) {
  if (!liveEl) return { strategy: 'foreignObject', reason: 'no-live-el', signature: '' };

  // (1) attribute override on the element itself
  const attrStrat = liveEl.getAttribute && liveEl.getAttribute('data-export-text-strategy');
  if (attrStrat) return { strategy: attrStrat, reason: 'data-attr-override', signature: '' };

  // (2) + (3) window-level overrides
  if (typeof window !== 'undefined' && window.__exportTextStrategy) {
    const id = liveEl.getAttribute('data-export-id');
    if (id && window.__exportTextStrategy[id]) {
      return { strategy: window.__exportTextStrategy[id], reason: 'window-override-id', signature: '' };
    }
    if (window.__exportTextStrategy.default) {
      return { strategy: window.__exportTextStrategy.default, reason: 'window-override-default', signature: '' };
    }
  }

  const signature = computeTextSignature(liveEl, cs);

  // (4) cache hit: a previous export already proved which strategy works
  // for this (font / weight / style / variation / script / engine) tuple.
  // Re-use it. Note `engineDefaultStrategy` is no longer consulted — we
  // no longer need engine-specific opt-outs because foreignObject is
  // already the default everywhere.
  const cached = getCachedStrategy(signature);
  if (cached) return { strategy: cached, reason: 'cache-hit', signature };
  // engineDefaultStrategy retained for backwards compat in callers that
  // import it; no longer consulted here. Silence the unused-import lint.
  void engineDefaultStrategy;

  // (5) Default: foreignObject. Browser HTML layout is the source of
  // truth — no reconstruction, no per-word X math, no synthesized wrap.
  return { strategy: 'foreignObject', reason: 'default-foreignObject', signature };
}

function applyTextTransform(text, transform) {
  if (!text) return text;
  switch (transform) {
    case 'uppercase': return text.toUpperCase();
    case 'lowercase': return text.toLowerCase();
    case 'capitalize':
      return text.replace(/(\p{L})(\p{L}*)/gu, (_, h, t) => h.toUpperCase() + t.toLowerCase());
    default: return text;
  }
}

/**
 * Read the live element's rendered word geometry, exactly as the browser
 * laid it out. **No layout decisions are made here** — every X/Y value
 * comes from `getBoundingClientRect()` on per-word Ranges.
 *
 * Returns: [{ top, height, words: [{ x, text }] }]
 *   - one entry per visual line (grouped by matching Y)
 *   - each word's `x` is the browser-computed left edge of that word
 *     within the element, in NATURAL canvas pixels (zoom de-scaled)
 *   - the gap between consecutive words' x values IS the rendered space
 *     width the browser used — preserved by per-word SVG positioning,
 *     so SVG never has to flow words or compute kerning/letter-spacing
 *
 * Whitespace runs are tracked only for their effect on Y (a whitespace
 * run that lands on the next line means a wrap happened) — they are
 * never emitted as text. The visual space between words is recreated
 * purely by the gap between word x positions.
 *
 * Edge cases handled:
 *   - editor zoom: rect coords are viewport-scaled; offsetWidth/visualWidth
 *     ratio gives the exact un-zoom factor
 *   - explicit \n breaks: detected via Y discontinuity
 *   - ligatures / kerning: per-word (not per-char) so combined glyphs stay together
 *   - text-transform: applied at emit time to each word's source text
 *   - elements with zero rendered size: returns []
 */
function getRenderedWordLines(liveDiv) {
  if (!liveDiv) return [];
  const text = liveDiv.textContent || '';
  if (!text) return [];

  const containerRect = liveDiv.getBoundingClientRect();
  if (containerRect.height === 0) return [];
  const containerLeft = containerRect.left;
  const containerTop  = containerRect.top;

  // Zoom de-scale: offsetWidth is natural CSS px (transform-unaffected);
  // containerRect.width is viewport px (transform-affected). Their ratio is
  // the un-zoom factor to recover natural canvas coordinates.
  const naturalWidth = liveDiv.offsetWidth;
  const visualWidth  = containerRect.width;
  const scale = (naturalWidth > 0 && visualWidth > 0) ? (naturalWidth / visualWidth) : 1;

  const lines = [];
  let cur = { top: null, height: 0, words: [] };

  const walker = document.createTreeWalker(liveDiv, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let node;
  while ((node = walker.nextNode())) {
    const data = node.data;
    if (!data) continue;
    const runs = data.match(/\S+|\s+/g);
    if (!runs) continue;

    let pos = 0;
    for (const run of runs) {
      const start = pos;
      const end = pos + run.length;
      pos = end;

      const isWhitespace = /^\s+$/.test(run);

      range.setStart(node, start);
      range.setEnd(node, end);
      const rect = range.getBoundingClientRect();

      // Zero-area runs (collapsed whitespace at a wrap point) carry no
      // useful geometry — skip outright. The line break they imply is
      // recovered from the NEXT non-whitespace run's Y discontinuity.
      if (rect.width === 0 && rect.height === 0) continue;

      const relX  = (rect.left - containerLeft) * scale;
      const relTop = (rect.top - containerTop) * scale;
      const relHeight = rect.height * scale;

      // Whitespace runs only inform line transitions; we never emit them.
      if (isWhitespace) {
        // If a whitespace run started a new line, flush the current line
        // and begin fresh — but don't push the whitespace itself.
        if (cur.top !== null && Math.abs(relTop - cur.top) >= 1) {
          if (cur.words.length > 0) lines.push(cur);
          cur = { top: relTop, height: relHeight, words: [] };
        }
        continue;
      }

      if (cur.top === null) {
        cur.top = relTop;
        cur.height = relHeight;
        cur.words.push({ x: relX, text: run });
      } else if (Math.abs(relTop - cur.top) < 1) {
        cur.words.push({ x: relX, text: run });
        if (relHeight > cur.height) cur.height = relHeight;
      } else {
        if (cur.words.length > 0) lines.push(cur);
        cur = { top: relTop, height: relHeight, words: [{ x: relX, text: run }] };
      }
    }
  }
  if (cur.top !== null && cur.words.length > 0) lines.push(cur);
  return lines;
}

function serializeOne(divEl, liveEl, failures) {
  // All metrics read from the LIVE element — its computed styles and
  // bounding rects are what the editor actually renders. The clone may
  // not be laid out yet (it's still being constructed by html2canvas).
  if (!liveEl) {
    failures.push({
      reason: 'no live element matched (data-export-id missing or stale)',
      info: { id: divEl.getAttribute('data-export-id') },
    });
    return false;
  }

  const cs = window.getComputedStyle(liveEl);
  if ((cs.writingMode || 'horizontal-tb') !== 'horizontal-tb') {
    failures.push({ reason: 'non-horizontal writing mode skipped', info: { writingMode: cs.writingMode } });
    return false;
  }

  const w = parseFloat(divEl.style.width)  || liveEl.offsetWidth  || 0;
  const h = parseFloat(divEl.style.height) || liveEl.offsetHeight || 0;
  if (w <= 0 || h <= 0) return false;

  const fontFamily    = cs.fontFamily || 'sans-serif';
  const fontSize      = parseFloat(cs.fontSize) || 16;
  const fontWeight    = cs.fontWeight || 'normal';
  const fontStyle     = cs.fontStyle  || 'normal';
  const letterSpacing = cs.letterSpacing === 'normal' ? 0 : (parseFloat(cs.letterSpacing) || 0);
  const wordSpacing   = cs.wordSpacing   === 'normal' ? 0 : (parseFloat(cs.wordSpacing)   || 0);
  const color         = cs.color || '#000';
  const opacity       = cs.opacity || '1';
  // textAlign intentionally not read — per-word emission uses the browser's
  // exact computed X positions, which already encode alignment. Recomputing
  // anchor math from textAlign would double-apply it.
  const textTransform = cs.textTransform || 'none';
  const textDeco      = (cs.textDecorationLine || cs.textDecoration || 'none').split(' ')[0];
  const fontVariant   = cs.fontVariantLigatures || 'normal';

  // Read the live element's rendered word geometry — never guess. Each
  // word carries the browser-computed X at which it was placed.
  const wordLines = getRenderedWordLines(liveEl);
  if (wordLines.length === 0) {
    // Element has no laid-out text (empty, hidden, or zero-size).
    // Leave the clone untouched — html2canvas will render the empty div.
    return false;
  }

  // Baseline for each line: lineBoxTop + half-leading + ascent.
  // For lineBoxHeight prefer the computed `line-height` (already resolved
  // by the metric normalizer to a px value) over the measured rect.height
  // — the rect can be artificially small under transforms or noise.
  const cssLineHeight = cs.lineHeight && cs.lineHeight !== 'normal'
    ? parseFloat(cs.lineHeight)
    : fontSize * 1.2;
  const measureCanvas = document.createElement('canvas').getContext('2d');
  measureCanvas.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
  const m = measureCanvas.measureText('Mg');
  const ascent = m.actualBoundingBoxAscent || fontSize * 0.8;
  const halfLeading = (cssLineHeight - fontSize) / 2;

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('xmlns', SVG_NS);
  svg.setAttribute('width',  String(w));
  svg.setAttribute('height', String(h));
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  // SVG root has implicit `overflow: hidden` per spec — neither
  // style.overflow nor a CSS rule overrides this on every renderer.
  // The attribute IS the spec-correct opt-out and is honoured by every
  // SVG implementation (including html2canvas's SVG path). Without it,
  // any word whose right edge exceeds the element width gets clipped.
  svg.setAttribute('overflow', 'visible');
  svg.style.position = 'absolute';
  svg.style.left = '0';
  svg.style.top  = '0';
  svg.style.display  = 'block';
  svg.style.overflow = 'visible';

  // Per-word emission: one <text> per visual line; inside, one <tspan>
  // per word with the word's exact browser-computed x. SVG renders each
  // word starting AT THAT X — it never flows / wraps / chooses break
  // points. The gap between words is exactly the space the browser used.
  // text-anchor is ALWAYS "start" since we already have the start x; we
  // never recompute centre/right alignment math.
  for (const line of wordLines) {
    if (line.words.length === 0) continue;
    const baselineY = line.top + halfLeading + ascent;

    const textEl = document.createElementNS(SVG_NS, 'text');
    textEl.setAttribute('y', String(baselineY));
    textEl.setAttribute('text-anchor', 'start');
    textEl.setAttribute('font-family', fontFamily);
    textEl.setAttribute('font-size',   String(fontSize));
    textEl.setAttribute('font-weight', String(fontWeight));
    textEl.setAttribute('font-style',  fontStyle);
    textEl.setAttribute('fill',        color);
    textEl.setAttribute('fill-opacity', opacity);
    if (letterSpacing) textEl.setAttribute('letter-spacing', String(letterSpacing));
    if (wordSpacing)   textEl.setAttribute('word-spacing',   String(wordSpacing));
    if (textDeco && textDeco !== 'none') textEl.setAttribute('text-decoration', textDeco);
    if (fontVariant && fontVariant !== 'normal') textEl.setAttribute('font-variant-ligatures', fontVariant);
    textEl.setAttribute('xml:space', 'preserve');
    textEl.setAttribute('dominant-baseline', 'alphabetic');

    for (const word of line.words) {
      const tspan = document.createElementNS(SVG_NS, 'tspan');
      // Explicit x forces this word's start position — overrides any
      // SVG-internal flowing of the preceding tspan's end.
      tspan.setAttribute('x', String(word.x));
      tspan.textContent = applyTextTransform(word.text, textTransform);
      textEl.appendChild(tspan);
    }
    svg.appendChild(textEl);
  }

  while (divEl.firstChild) divEl.removeChild(divEl.firstChild);
  divEl.appendChild(svg);
  // Stop the host div from also painting its own text-color background once
  // we've replaced its content with SVG — color now lives in `fill`.
  divEl.style.color = 'transparent';
  return true;
}

export function serializeTextElementsForExport(liveRoot, clonedRoot) {
  const result = {
    converted: 0,
    failures: [],
    skippedGradient: 0,
    skippedNoLive: 0,
    perText: [],          // [{ id, strategy, reason, ok }]
    strategyCounts: {},   // { 'svg-word': N, 'foreignObject': N, … }
  };
  // Backwards-compat: if called with only one arg, treat it as cloned root
  // and skip live-element lookup (degrades to a no-op for all elements).
  if (!clonedRoot && liveRoot) {
    clonedRoot = liveRoot;
    liveRoot = null;
  }
  if (!clonedRoot || !clonedRoot.querySelectorAll) return result;

  const diag = typeof window !== 'undefined' && window.__exportDiagnostics;

  // Deterministic: only elements the editor explicitly tagged as text.
  const candidates = clonedRoot.querySelectorAll('[data-export-type="text"]');
  for (const cloneEl of candidates) {
    if (!cloneEl.style) continue;
    // Gradient text path: CanvasEditor emits an inner <svg><text> with
    // gradient defs for fillGradient/strokeGradient. That SVG renders
    // faithfully through html2canvas's SVG path — leave it untouched.
    if (cloneEl.querySelector('svg')) {
      result.skippedGradient++;
      continue;
    }
    const bg = cloneEl.style.backgroundImage || '';
    if (bg.includes('gradient(')) {
      result.failures.push({
        reason: 'css-gradient-text-deferred',
        info: { id: cloneEl.getAttribute('data-export-id'), background: bg },
      });
      continue;
    }
    const text = cloneEl.textContent;
    if (!text || !text.trim()) continue;

    // Locate the matching live element via the deterministic export id.
    const exportId = cloneEl.getAttribute('data-export-id');
    let liveEl = null;
    if (exportId && liveRoot && liveRoot.querySelector) {
      liveEl = liveRoot.querySelector(`[data-export-id="${CSS.escape(exportId)}"]`);
    }
    if (!liveEl) {
      result.skippedNoLive++;
      // Without a live element we can't read actual wrap; safer to leave
      // the clone alone (html2canvas will render its text, which is the
      // pre-fix behaviour) than to emit wrong lines.
      continue;
    }

    // Pick the rendering strategy per-element. Default is foreignObject;
    // svg-word is only chosen via explicit override.
    const cs = window.getComputedStyle(liveEl);
    const { strategy, reason } = pickTextStrategy(liveEl, cs);
    let actualStrategy = strategy;
    let ok = false;
    let fellBack = false;

    const runStrategy = (strat) => {
      if (strat === 'foreignObject') {
        return serializeTextAsForeignObject(cloneEl, liveEl, result.failures);
      }
      return serializeOne(cloneEl, liveEl, result.failures);
    };

    try {
      ok = runStrategy(strategy);
    } catch (e) {
      result.failures.push({
        reason: 'serialize threw',
        info: { id: exportId, strategy, message: String(e?.message || e) },
      });
    }

    // Per-node fallback ONLY — never downgrades the whole canvas.
    // If foreignObject failed for this node (text-content mismatch,
    // unsupported subtree, etc.), retry just this node with svg-word.
    // The fallback is logged with a structured failure record + a
    // whitespace diagnostic so the cause can be reproduced.
    if (!ok && strategy === 'foreignObject') {
      try {
        const ws = whitespaceDiagnostic(liveEl);
        // eslint-disable-next-line no-console
        console.warn('[export-parity-fail:text-content]', {
          id: exportId,
          attemptedStrategy: 'foreignObject',
          fallback: 'svg-word',
          whitespace: ws,
        });
        // Restore the host div so svg-word can re-mutate it cleanly —
        // foreignObject may have emptied it before failing the invariant.
        while (cloneEl.firstChild) cloneEl.removeChild(cloneEl.firstChild);
        // Copy the live text back so svg-word's getRenderedWordLines has
        // something to read off the LIVE side regardless.
        ok = runStrategy('svg-word');
        if (ok) { actualStrategy = 'svg-word'; fellBack = true; }
      } catch (e) {
        result.failures.push({
          reason: 'fallback-svg-word-threw',
          info: { id: exportId, message: String(e?.message || e) },
        });
      }
    }

    if (ok) result.converted++;
    result.strategyCounts[actualStrategy] =
      (result.strategyCounts[actualStrategy] || 0) + 1;
    result.perText.push({ id: exportId, strategy: actualStrategy, reason, ok, fellBack });

    // Per-node diagnostic — makes it deterministic which renderer branch
    // handled each text node, and why. The format matches the brief:
    //   [text-export] id=… strategy=… reason=… ok=…
    if (diag) {
      // eslint-disable-next-line no-console
      console.log('[text-export]', {
        id: exportId, strategy: actualStrategy, reason, ok, fellBack,
      });
    }
  }
  return result;
}
